/**
 * Canonical feedback status values. Single source of truth: the rpc
 * contract builds its zod enum from this array and the Sequelize model types
 * its `status` column against the derived union, so adding a status here
 * flows to both sides.
 */
export const FEEDBACK_STATUSES = [
  "new",
  "in_triage",
  "backlog",
  "done",
  "canceled",
  "duplicate",
] as const;

export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/**
 * What a piece of feedback is. Chosen by the submitter, re-labelled by
 * triagers; "bug" is one kind among others, not the whole feature.
 */
export const FEEDBACK_KINDS = [
  "bug",
  "feature_request",
  "question",
  "other",
] as const;

export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/**
 * Statuses that close a report. Entering one stamps `resolvedAt` /
 * `resolvedByUserId`; leaving one clears them.
 */
export const FEEDBACK_TERMINAL_STATUSES: readonly FeedbackStatus[] = [
  "done",
  "canceled",
  "duplicate",
];

export function isTerminalFeedbackStatus(status: FeedbackStatus): boolean {
  return FEEDBACK_TERMINAL_STATUSES.includes(status);
}

/**
 * Open statuses that stop the resolution clock: the team looked at the
 * report and chose to park it, so the time it waits there is not time spent
 * resolving it. A new "waiting on someone else" status belongs here too.
 */
export const FEEDBACK_PARKED_STATUSES: readonly FeedbackStatus[] = ["backlog"];

export function isParkedFeedbackStatus(status: string): boolean {
  return (FEEDBACK_PARKED_STATUSES as readonly string[]).includes(status);
}

/** Prefix of the human-readable ticket number shown on every feedback. */
export const FEEDBACK_NUMBER_PREFIX = "FB-";

/** `12` → `FB-12`; null (a row from before numbering) → `FB-?`. */
export function formatFeedbackNumber(
  number: number | null | undefined,
): string {
  return number == null
    ? `${FEEDBACK_NUMBER_PREFIX}?`
    : `${FEEDBACK_NUMBER_PREFIX}${number}`;
}

const FEEDBACK_NUMBER_RE = /^\s*(?:#|fb-?)?\s*(\d{1,9})\s*$/i;

/**
 * The ticket number a person typed, in any of the shapes people write it:
 * `12`, `#12`, `FB-12`, `fb12`. Null for anything else (a uuid, a word).
 */
export function parseFeedbackNumber(text: string): number | null {
  const match = FEEDBACK_NUMBER_RE.exec(text);
  return match ? Number(match[1]) : null;
}
